import { AutoColumn } from 'components/Layout/Column'
import { RowFixed } from 'components/Layout/Row'
import { CurrencyLogo } from 'components/Logo'
import { TagComponent, tagTypes } from 'components/Tags'
import { TokenPairImage } from 'components/TokenImage'
import { FIXED_REMOVAL_FEE_PECENTAGE } from 'config/constants/burn'
import { useTranslation } from 'contexts/Localization'
import useToast from 'hooks/useToast'
import useTotalSupply from 'hooks/useTotalSupply'
import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ClpField, Field } from 'state/burn/actions'
import { useBurnActionHandlers, useSmartRemovalInputActionHandlers, useSnapshot } from 'state/burn/hooks'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import { currencyId } from 'utils/currencyId'
import { getBalanceAmount } from 'utils/formatBalance'
import { unwrappedToken } from 'utils/wrappedCurrency'
import useClpHandlers from 'views/RemoveLiquidity/hooks/useClpHandlers'
import useSmartApr from 'views/RemoveLiquidity/hooks/useSmartApr'
import { TokenAmount } from 'vvs-sdk'
import {
  AutoRenewIcon,
  Button,
  CardBody,
  Flex,
  Text,
  TooltipText,
  useTooltip,
  TokenAmount as Amount,
  ErrorIcon,
} from 'vvs-uikit'
import { FixedHeightRow, FixedHeightRowEnd, StyledCard } from '../styles'
import { LiquidityCardProps } from '../types'

const getSign = (percent: number) => (percent > 0 ? '+' : percent < 0 ? '-' : '±')

const RemovalOrderCard: React.FC<LiquidityCardProps> = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { onDeposit } = useClpHandlers()
  const { toastError } = useToast()

  const { onUserInput } = useBurnActionHandlers()
  const { onUserInput: _onSmartRemovalInput } = useSmartRemovalInputActionHandlers()

  const vvsPrice = usePriceVvsUsdc()
  const [pendingTx, setPendingTx] = useState(false)
  const { userData, pair, onCancel } = props

  const error = !userData.hasRuleEnabled

  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)

  const snapshot = useSnapshot(`${pair.token0.address}-${pair.token1.address}`)

  const totalSupply = useTotalSupply(pair?.liquidityToken)
  const pooledToken0 =
    totalSupply &&
    pair?.getLiquidityValue(
      pair.token0,
      totalSupply,
      new TokenAmount(pair.liquidityToken, userData.stakedBalance.toString()),
      false,
    )
  const pooledToken1 =
    totalSupply &&
    pair?.getLiquidityValue(
      pair.token1,
      totalSupply,
      new TokenAmount(pair.liquidityToken, userData.stakedBalance.toString()),
      false,
    )
  const [token0Denom, token1Denom] = [
    parseFloat(pooledToken0?.toSignificant(6)),
    parseFloat(pooledToken1?.toSignificant(6)),
  ]

  const formattedAmounts = {
    stakedBalance: userData.stakedBalance.isEqualTo('0')
      ? '0'
      : new TokenAmount(pair.liquidityToken, userData.stakedBalance.toString()).toSignificant(6),
    token0Amt: snapshot
      ? snapshot.inputTokenA
      : userData.token0Amt.isEqualTo('0')
      ? '-'
      : new TokenAmount(pair.token0, userData.token0Amt.toString()).toSignificant(6),
    token1Amt: snapshot
      ? snapshot.inputTokenB
      : userData.token1Amt.isEqualTo('0')
      ? '-'
      : new TokenAmount(pair.token1, userData.token1Amt.toString()).toSignificant(6),
    targetPriceToken0: snapshot
      ? snapshot.TARGET_PRICE_TOKEN_A
      : userData.token0Amt.isEqualTo('0')
      ? '-'
      : new TokenAmount(pair.token1, userData.token1Amt.toString())
          .divide(new TokenAmount(pair.token0, userData.token0Amt.toString()))
          .toSignificant(6),
    targetPriceToken1: snapshot
      ? snapshot.TARGET_PRICE_TOKEN_B
      : userData.token1Amt.isEqualTo('0')
      ? '-'
      : new TokenAmount(pair.token0, userData.token0Amt.toString())
          .divide(new TokenAmount(pair.token1, userData.token1Amt.toString()))
          .toSignificant(6),
    deltaToken0: snapshot
      ? snapshot.DELTA_TOKEN_A
      : ((parseFloat(new TokenAmount(pair.token0, userData.token0Amt.toString()).toSignificant(6)) - token0Denom) /
          token0Denom) *
        100,
    deltaToken1: snapshot
      ? snapshot.DELTA_TOKEN_B
      : ((parseFloat(new TokenAmount(pair.token1, userData.token1Amt.toString()).toSignificant(6)) - token1Denom) /
          token1Denom) *
        100,
    pendingVvs: getBalanceAmount(userData.pendingVvs).toNumber().toLocaleString(),
    pendingVvsUsdc: getBalanceAmount(userData.pendingVvs).multipliedBy(vvsPrice).toNumber().toLocaleString(),
  }

  const { farmApr } = useSmartApr(pair)

  const addTransaction = useTransactionAdder()
  const handleHarvest = useCallback(async () => {
    setPendingTx(true)
    try {
      const depositRes = await onDeposit(pair.liquidityToken.address, '0')
      addTransaction(depositRes, {
        summary: t('Your reward have been deposited to your wallet'),
      })
    } catch (err: any) {
      if (err?.code !== 4001) {
        console.error(err)
        toastError(t('Transaction Failed'), t('Please try again later'))
      }
    } finally {
      setPendingTx(false)
    }
  }, [addTransaction, onDeposit, pair.liquidityToken.address, t, toastError])

  const tooltipContent = t(
    t(
      'While your LP tokens are pending removal, you will continue to accrue VVS rewards at the displayed APR rate! A small fee that is already subtracted from the smart APR goes to cover the operations of the feature. Your pending rewards can be harvested in the ‘Removal Orders’ tab.',
    ),
  )
  const { targetRef, tooltip, tooltipVisible } = useTooltip(tooltipContent, { placement: 'auto' })

  const handleResubmit = () => {
    onUserInput(
      Field.LIQUIDITY,
      new TokenAmount(pair.liquidityToken, userData.stakedBalance.toString()).toSignificant(
        pair.liquidityToken.decimals,
      ),
    )
    if (snapshot) {
      _onSmartRemovalInput(ClpField.CURRENCY_A, snapshot.inputTokenA)
      _onSmartRemovalInput(ClpField.CURRENCY_B, snapshot.inputTokenB)
    }
    history.push(`/remove/smart/retry/${currencyId(currency0)}/${currencyId(currency1)}`)
  }

  return (
    <StyledCard>
      <CardBody p={0}>
        <AutoColumn gap="8px" style={{ padding: '16px' }}>
          <Flex marginBottom="15px" justifyContent="space-between">
            <TagComponent title={t('Removal Order')} variant={tagTypes.PRIMARY} />
          </Flex>

          <Flex justifyContent="space-between" alignItems="center">
            <TokenPairImage
              variant="inverted"
              primaryToken={pair.token0}
              secondaryToken={pair.token1}
              width={32}
              height={32}
            />
            <Text fontSize="20px" bold>
              {`${currency0.symbol}/${currency1.symbol}`}
            </Text>
          </Flex>

          <Flex justifyContent="flex-start" role="button" my={3}>
            <Text fontSize="20px" bold>
              {formattedAmounts.stakedBalance}
            </Text>
          </Flex>

          <FixedHeightRow>
            <Text mb={1} textTransform="uppercase">
              {t('You will receive')}
            </Text>
          </FixedHeightRow>

          {error ? (
            <>
              <Flex justifyContent="center" alignItems="center" padding="18px" background="#F0F8F9" borderRadius="12px">
                <ErrorIcon color="primary" mr={1} height={16} width={16} />
                <Text small bold color="black" textAlign="center" fontSize="13px" display="inline-block">
                  {t('Please confirm this transaction in your wallet')}
                </Text>
              </Flex>
              <Button scale="lg" variant="primary" width="100%" mb="8px" onClick={handleResubmit}>
                {t('Confirm Removal Order')}
              </Button>
            </>
          ) : (
            <>
              <FixedHeightRow>
                <RowFixed>
                  <CurrencyLogo size="20px" currency={currency0} />
                  <Text color="textSubtle" ml="4px">
                    {currency0.symbol}
                  </Text>
                </RowFixed>
                {userData.token0Amt ? (
                  <RowFixed>
                    <Text ml="6px">{formattedAmounts.token0Amt}</Text>
                  </RowFixed>
                ) : (
                  '-'
                )}
              </FixedHeightRow>
              <FixedHeightRowEnd>
                <Text color="textSubtle">
                  {t('(Target: %percent%%)', {
                    percent:
                      !snapshot &&
                      (userData.token1Amt.isEqualTo('0') ||
                        userData.token1Amt.isEqualTo('0') ||
                        (formattedAmounts.deltaToken0 !== 0 && !formattedAmounts.deltaToken0))
                        ? '-'
                        : `${getSign(formattedAmounts.deltaToken0)}${Math.abs(formattedAmounts.deltaToken0).toFixed(
                            2,
                          )}`,
                  })}
                </Text>
              </FixedHeightRowEnd>

              <FixedHeightRow>
                <RowFixed>
                  <CurrencyLogo size="20px" currency={currency1} />
                  <Text color="textSubtle" ml="4px">
                    {currency1.symbol}
                  </Text>
                </RowFixed>
                {userData.token1Amt ? (
                  <RowFixed>
                    <Text ml="6px">{formattedAmounts.token1Amt}</Text>
                  </RowFixed>
                ) : (
                  '-'
                )}
              </FixedHeightRow>
              <FixedHeightRowEnd>
                <Text color="textSubtle">
                  {t('(Target: %percent%%', {
                    percent:
                      !snapshot &&
                      (userData.token1Amt.isEqualTo('0') ||
                        userData.token1Amt.isEqualTo('0') ||
                        (formattedAmounts.deltaToken1 !== 0 && !formattedAmounts.deltaToken1))
                        ? '-'
                        : `${getSign(formattedAmounts.deltaToken1)}${Math.abs(formattedAmounts.deltaToken1).toFixed(
                            2,
                          )}`,
                  })}
                </Text>
              </FixedHeightRowEnd>

              <FixedHeightRow mb={3}>
                {tooltipVisible && tooltip}
                <TooltipText small color="textSubtle" ref={targetRef}>
                  {t('Smart APR')}
                </TooltipText>
                <Text>{`${(farmApr * ((100 - FIXED_REMOVAL_FEE_PECENTAGE) / 100)).toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                })}%`}</Text>
              </FixedHeightRow>
            </>
          )}

          <FixedHeightRow>
            <Text mb={1} textTransform="uppercase">
              {t('VVS Earned')}
            </Text>
          </FixedHeightRow>

          <Flex justifyContent="space-between" alignItems="center" mb="16px">
            <Flex flexDirection="column">
              <Amount color="textSubtle">{formattedAmounts.pendingVvs}</Amount>
              <Text fontSize="12px" color="textSubtle">
                {formattedAmounts.pendingVvsUsdc} USD
              </Text>
            </Flex>
            <Button
              onClick={handleHarvest}
              disabled={userData.pendingVvs.isEqualTo('0')}
              isLoading={pendingTx}
              endIcon={pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
            >
              {t('Harvest')}
            </Button>
          </Flex>

          {userData.stakedBalance && userData.stakedBalance.isGreaterThan('0') && (
            <Flex flexDirection={error ? 'row' : 'column'} justifyContent="space-between">
              <Button
                scale="lg"
                onClick={() =>
                  onCancel(
                    new TokenAmount(pair.liquidityToken, userData.stakedBalance.toString()).toFixed(
                      pair.liquidityToken.decimals,
                    ),
                    pair,
                  )
                }
                variant="primary"
                width="100%"
                mb="8px"
              >
                {t(error ? 'Unstake' : 'Cancel Order')}
              </Button>
            </Flex>
          )}
        </AutoColumn>
      </CardBody>
    </StyledCard>
  )
}

export default RemovalOrderCard
